import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { toast } from "react-toastify";
import { CommonButton } from "~/components/ui/commonButton";
import { Loading } from "~/components/ui/loading";
import Modal from "~/components/ui/modal";
import { Pagination } from "~/components/ui/pagination";
import { TOAST_MESSAGES } from "~/constants/messages";
import { planMap } from "~/constants/planMap";
import CancelReqxignModal from "~/features/billings/cancelReqxign";
import { OrganizationApi } from "~/functions/api/organization";
import { useCredits } from "~/hooks/useCredits";
import CheckIcon from "~/images/check.svg?react";
import ReloadIcon from "~/images/reload.svg?react";
import UserIcon from "~/images/user.svg?react";
import type { Credit } from "~/interface/credit";
import type { Organization } from "~/interface/organization";
import type { User } from "~/interface/user";
import { setOrganization } from "~/slices/auth";
import type { RootState } from "~/store";
import styles from "./index.module.scss";

const tabNames = ["overview", "credits"];

const Billings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { organization, status } = useSelector((state: RootState) => state.auth);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tabQuery = searchParams.get("tab");

  // クエリに対応するタブの index を計算
  const initialIndex = useMemo(() => {
    const index = tabNames.indexOf(tabQuery || "");
    return index >= 0 ? index : 0;
  }, [tabQuery]);

  // 現在のパスからタブを選択
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);

  // URLのクエリとstateを同期（タブ変更時）
  const handleTabChange = (index: number) => {
    const tab = tabNames[index];
    if (tab) {
      searchParams.set("tab", tab);
      navigate({ pathname: location.pathname, search: searchParams.toString() }, { replace: true });
    }
    setSelectedIndex(index);
  };

  // Sync selected tab from URL query without triggering navigation
  useEffect(() => {
    const idx = tabNames.indexOf(tabQuery || "");
    const next = idx >= 0 ? idx : 0;
    setSelectedIndex((prev) => (prev === next ? prev : next));
  }, [tabQuery]);

  if (status === "loading" || !organization) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-6 text-gray-900">支払いとプラン</h1>
        <div className="mt-4">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-6 text-gray-900">支払いとプラン</h1>
      <Tabs className={styles["tab"]} selectedIndex={selectedIndex} onSelect={handleTabChange}>
        <TabList className={styles["tab-list"]}>
          <Tab
            className={`${styles["tab-list-name"]} min-w-[150px] flex justify-center`}
            selectedClassName={styles["tab-list-name--selected"]}
          >
            概要
          </Tab>
          <Tab
            className={styles["tab-list-name"]}
            selectedClassName={styles["tab-list-name--selected"]}
          >
            クレジット履歴
          </Tab>
          {/* <Tab
            className={styles["tab-list-name"]}
            selectedClassName={styles["tab-list-name--selected"]}
          >
            支払い情報
          </Tab> */}
          {/* <Tab
            className={styles["tab-list-name"]}
            selectedClassName={styles["tab-list-name--selected"]}
          >
            支払い履歴
          </Tab> */}
        </TabList>
        <TabPanel>
          <BillingOverviewTabContent />
        </TabPanel>
        <TabPanel>
          <CreditHistoryTabContent />
        </TabPanel>
        {/* <TabPanel>支払い情報</TabPanel> */}
        {/* <TabPanel>支払い履歴</TabPanel> */}
      </Tabs>
    </div>
  );
};

// 概要タブのコンテンツ
const BillingOverviewTabContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    fetchCurrentCredits,
    currentCredits,
    loading,
    error,
    // totalCount,
  } = useCredits();

  const { organization, currentUser } = useSelector((state: RootState) => state.auth);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isChatRequestModalOpen, setIsChatRequestModalOpen] = useState(false);

  // const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // Fetch recent credits once on mount
  const initCreditsRef = useRef(false);
  useEffect(() => {
    if (initCreditsRef.current) return;
    initCreditsRef.current = true;
    fetchCurrentCredits();
  }, [fetchCurrentCredits]);

  // 「すべて見る」ボタン押下時のハンドラー
  const handleClickAllCreditsButton = () => {
    const newParams = new URLSearchParams(location.search);
    newParams.set("tab", "credits");
    navigate({ pathname: location.pathname, search: newParams.toString() }, { replace: true });
  };

  return (
    <div>
      <div className={styles["overviewGrid"]}>
        <div className="flex-1">{PlanDescriptionCard()}</div>
        <div className="flex-1">
          <CreditDescriptionCard />
        </div>
        <div className="flex-1">
          <ChatRequestDescriptionCard setIsChatRequestModalOpen={setIsChatRequestModalOpen} />
        </div>
        <div className="flex-1">
          <AppGenerationDescriptionCard />
        </div>
      </div>
      <h3 className="pt-5 pb-3 text-base font-medium">最近のクレジット履歴</h3>
      {CreditsTable(currentCredits, loading, error)}
      <button
        type="button"
        onClick={handleClickAllCreditsButton}
        className="w-[85px] h-[34px] min-w-[80px] rounded-[4px] border border-solid border-borderblue text-lightblue gap-[1px] p-[2px]"
      >
        <span className="text-sm font-medium">すべて見る</span>
      </button>

      <CancelReqxignModal isOpen={isModalOpen} onClose={closeModal} />
      <Modal
        label="リクエストを追加"
        isOpen={isChatRequestModalOpen}
        onClose={() => setIsChatRequestModalOpen(false)}
        content={
          <ChatRequestConvertModal
            organization={organization}
            currentUser={currentUser}
            onClose={() => setIsChatRequestModalOpen(false)}
          />
        }
        className="chatRequestConvertModal"
      />
    </div>
  );
};

// プランの説明カード
const PlanDescriptionCard = () => {
  const navigate = useNavigate();
  const { organization, currentUser } = useSelector((state: RootState) => state.auth);
  const limits = organization?.plan?.limits;
  const planType = organization?.plan?.type || "free";
  const isAdmin = currentUser?.role === "admin";

  if (!organization || !limits) {
    return (
      <div className={styles["descriptionCard"]}>
        <Loading />
      </div>
    );
  }

  return (
    <div className={styles["descriptionCard"]}>
      <div className={styles["descriptionCard__header"]}>
        <p className={styles["descriptionCard__title"]}>プラン</p>
      </div>
      <div className={styles["descriptionCard__body"]}>
        <p className="font-medium text-sm">{planMap[planType]?.name || planType}</p>
        <div className="flex flex-col gap-3 pb-2 flex-grow">
          <div className={styles["featureItem"]}>
            <CheckIcon className={styles["featureItem__icon"]} />
            <p className={styles["featureItem__text"]}>
              {limits.maxMembers.toLocaleString()}ユーザー数
            </p>
          </div>
          <div className={styles["featureItem"]}>
            <CheckIcon className={styles["featureItem__icon"]} />
            <p className={styles["featureItem__text"]}>
              {limits.maxProjects.toLocaleString()}プロジェクト
            </p>
          </div>
          <div className={styles["featureItem"]}>
            <CheckIcon className={styles["featureItem__icon"]} />
            <p className={styles["featureItem__text"]}>
              毎月{limits.monthlyLimit.toLocaleString()}クレジット付与
            </p>
          </div>
        </div>
      </div>
      {isAdmin && (
        <div className={styles["descriptionCard__action"]}>
          <button
            type="button"
            onClick={() => navigate(`/${organization.id}/billings/plan-change`)}
            className="w-full max-w-[135px] bg-[#1D9FD6] text-white font-bold rounded-[4px] p-2 text-sm cursor-pointer transition-colors duration-300 disabled:bg-gray-300 disabled:cursor-not-allowed self-start"
          >
            プラン変更
          </button>
        </div>
      )}
      {/* <div className="bg-bglightblue rounded-[4px] p-2 mt-2 text-[12px] font-medium">
        <p>💡現在の使い方だと<span className="text-lightblue">プロフェッショナルプラン</span>がおすすめ</p>
      </div> */}
    </div>
  );
};

// クレジット履歴タブのコンテンツ
const CreditHistoryTabContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const queryPage = parseInt(searchParams.get("page") || "1", 10);
  const { organization, currentUser } = useSelector((state: RootState) => state.auth);
  const isAdmin = currentUser?.role === "admin";
  const { credits, loading, error, totalCount, page, changePage } = useCredits();

  // 初期表示時、ページ変更時にクレジットを取得（同じページ値では再取得しない）
  const lastAppliedPageRef = useRef<number | null>(null);
  useEffect(() => {
    if (lastAppliedPageRef.current === queryPage) return;
    lastAppliedPageRef.current = queryPage;
    changePage(queryPage);
  }, [queryPage, changePage]);

  // ページ変更時のハンドラー（URLへの反映のみ）
  const handlePageChange = (newPage: number) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set("page", String(newPage));
    navigate({ pathname: location.pathname, search: newParams.toString() }, { replace: true });
  };

  return (
    <div>
      <div className="flex flex-row gap-8 pb-4 items-center">
        <CreditProgressBar />
        {isAdmin && organization.plan.type !== "free" && (
          <button
            type="button"
            onClick={() => navigate(`/${organization.id}/billings/credit-purchase`)}
            className="max-w-[135px] max-h-9 bg-bgblue text-white font-bold rounded-[4px] p-2 text-sm cursor-pointer transition-colors duration-300 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            クレジットを購入
          </button>
        )}
      </div>
      {CreditsTable(credits, loading, error)}
      {Pagination({
        totalCount,
        limit: 10,
        currentPage: page,
        onChangePage: handlePageChange,
      })}
    </div>
  );
};

// クレジットの説明カード
const CreditDescriptionCard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { organization, currentUser } = useSelector((state: RootState) => state.auth);
  const isAdmin = currentUser?.role === "admin";

  const handleReload = async () => {
    const organizationApi = new OrganizationApi();
    const updatedOrganization = await organizationApi.get(organization.id);

    if (updatedOrganization) {
      dispatch(setOrganization(updatedOrganization));
      toast.success(TOAST_MESSAGES.BILLING.CREDIT_UPDATE_SUCCESS);
    } else {
      toast.error(TOAST_MESSAGES.BILLING.CREDIT_UPDATE_FAILED);
    }
  };

  if (!organization || !organization.plan || !organization.plan.limits) {
    return (
      <div className={styles["descriptionCard"]}>
        <Loading />
      </div>
    );
  }

  return (
    <div className={styles["descriptionCard"]}>
      <div className={styles["descriptionCard__header"]}>
        <p className={styles["descriptionCard__title"]}>クレジット</p>
        <button
          type="button"
          onClick={handleReload}
          style={{ cursor: "pointer", background: "none", border: "none", padding: 0 }}
        >
          <ReloadIcon width={16} height={16} />
        </button>
      </div>
      <div className={styles["descriptionCard__body"]}>
        <CreditProgressBar />
        <div className="pb-2">
          <p className="text-sm pb-1 pt-1">クレジット付与数</p>
          <p className="text-sm">
            {organization.plan.limits.monthlyLimit.toLocaleString()}（毎月1日に付与）
          </p>
        </div>
      </div>
      {isAdmin && organization.plan.type !== "free" && (
        <div className={styles["descriptionCard__action"]}>
          <button
            type="button"
            onClick={() => navigate(`/${organization.id}/billings/credit-purchase`)}
            className="w-full max-w-[135px] bg-bgblue text-white font-bold rounded-[4px] p-2 text-sm cursor-pointer transition-colors duration-300 disabled:bg-gray-300 disabled:cursor-not-allowed self-start"
          >
            クレジットを購入
          </button>
        </div>
      )}
    </div>
  );
};

// アプリ生成の説明カード
const AppGenerationDescriptionCard: React.FC = () => {
  const dispatch = useDispatch();
  const { organization } = useSelector((state: RootState) => state.auth);

  const handleReload = async () => {
    const organizationApi = new OrganizationApi();
    const updatedOrganization = await organizationApi.get(organization.id);

    if (updatedOrganization) {
      dispatch(setOrganization(updatedOrganization));
      toast.success(TOAST_MESSAGES.BILLING.APP_GENERATION_UPDATE_SUCCESS);
    } else {
      toast.error(TOAST_MESSAGES.BILLING.APP_GENERATION_UPDATE_FAILED);
    }
  };

  if (!organization || !organization.plan || !organization.plan.limits) {
    return (
      <div className={styles["descriptionCard"]}>
        <Loading />
      </div>
    );
  }

  const limits = organization.plan.limits;
  const remaining = organization.remainingAppGenerationCount;
  const max = limits.appGenerationCount;
  const percent = remaining >= max ? 100 : (remaining / max) * 100;

  return (
    <div className={styles["descriptionCard"]}>
      <div className={styles["descriptionCard__header"]}>
        <p className={styles["descriptionCard__title"]}>アプリ生成</p>
        <button
          type="button"
          onClick={handleReload}
          style={{ cursor: "pointer", background: "none", border: "none", padding: 0 }}
        >
          <ReloadIcon width={16} height={16} />
        </button>
      </div>
      <div className={styles["descriptionCard__body"]}>
        <div className={styles["progressBar"]}>
          <div className={styles["progressBar__header"]}>
            <p className={styles["progressBar__label"]}>残りアプリ生成回数</p>
            <p className={styles["progressBar__value"]}>
              <span className={styles["progressBar__accent"]}>{remaining.toLocaleString()}</span>/
              {max.toLocaleString()}
            </p>
          </div>
          <div className={styles["progressBar__track"]}>
            <div className={styles["progressBar__fill"]} style={{ width: `${percent}%` }} />
          </div>
        </div>
        <div className="pb-2">
          <p className="text-sm pb-1 pt-1">アプリ生成利用回数</p>
          <p className="text-sm">{max.toLocaleString()}回（プラン上限）</p>
        </div>
        <p className={styles["creditNote"]}>
          ※利用回数が上限に達しても、{limits.appGenerationCreditCost}
          クレジットを消費してアプリ生成を利用できます。
        </p>
      </div>
    </div>
  );
};

// チャットリクエストの説明カード
interface ChatRequestDescriptionCardProps {
  setIsChatRequestModalOpen: (val: boolean) => void;
}

const ChatRequestDescriptionCard: React.FC<ChatRequestDescriptionCardProps> = ({
  setIsChatRequestModalOpen,
}) => {
  const dispatch = useDispatch();
  const { organization, currentUser } = useSelector((state: RootState) => state.auth);
  const isAdmin = currentUser?.role === "admin";
  const handleReload = async () => {
    const organizationApi = new OrganizationApi();
    const updatedOrganization = await organizationApi.get(organization.id);

    if (updatedOrganization) {
      dispatch(setOrganization(updatedOrganization));
      toast.success(TOAST_MESSAGES.BILLING.CHAT_REQUEST_UPDATE_SUCCESS);
    } else {
      toast.error(TOAST_MESSAGES.BILLING.CHAT_REQUEST_UPDATE_FAILED);
    }
  };

  if (!organization || !organization.plan || !organization.plan.limits) {
    return (
      <div className={styles["descriptionCard"]}>
        <Loading />
      </div>
    );
  }

  return (
    <div className={styles["descriptionCard"]}>
      <div className={styles["descriptionCard__header"]}>
        <p className={styles["descriptionCard__title"]}>チャットリクエスト</p>
        <button
          type="button"
          onClick={handleReload}
          style={{ cursor: "pointer", background: "none", border: "none", padding: 0 }}
        >
          <ReloadIcon width={16} height={16} />
        </button>
      </div>
      <div className={styles["descriptionCard__body"]}>
        <ChatRequestProgressBar />
        <div className="pb-2">
          <p className="text-sm pb-1 pt-1">チャットリクエスト数</p>
          <p className="text-sm">
            {organization.plan.limits.monthlyRequestLimit.toLocaleString()}（毎月1日に付与）
          </p>
        </div>
        <p className={styles["infoNote"]}>
          ※クレジットを消費してチャットリクエストを追加できます。
        </p>
      </div>
      {isAdmin && (
        <div className={styles["descriptionCard__action"]}>
          <button
            type="button"
            onClick={() => setIsChatRequestModalOpen(true)}
            className="w-full max-w-[135px] bg-bgblue text-white font-bold rounded-[4px] p-2 text-sm cursor-pointer transition-colors duration-300 disabled:bg-gray-300 disabled:cursor-not-allowed self-start"
          >
            リクエストを追加
          </button>
        </div>
      )}
    </div>
  );
};

// クレジットの消費割合バー
const CreditProgressBar = () => {
  const organization = useSelector((state: RootState) => state.auth.organization);
  const maxCredit = organization.plan.type !== "free" ? organization.plan.limits.monthlyLimit : 150;
  const remainingPercent =
    organization.remainingCredit >= maxCredit
      ? 100
      : (organization.remainingCredit / maxCredit) * 100;

  return (
    <div className={styles["progressBar"]}>
      <div className={styles["progressBar__header"]}>
        <p className={styles["progressBar__label"]}>残りクレジット</p>
        <p className={styles["progressBar__value"]}>
          <span className={styles["progressBar__accent"]}>
            {organization.remainingCredit.toLocaleString()}
          </span>
          {organization.plan.type !== "free" ? (
            <>/{organization.plan.limits.monthlyLimit.toLocaleString()}</>
          ) : (
            <>/ 150</>
          )}
        </p>
      </div>
      <div className={styles["progressBar__track"]}>
        <div className={styles["progressBar__fill"]} style={{ width: `${remainingPercent}%` }} />
      </div>
    </div>
  );
};

// チャットリクエストの消費割合バー
const ChatRequestProgressBar = () => {
  const organization = useSelector((state: RootState) => state.auth.organization);

  const maxRequest =
    organization.plan.type !== "free" ? organization.plan.limits.monthlyRequestLimit : 50;
  const remainingPercent =
    organization.remainingChatRequests >= maxRequest
      ? 100
      : (organization.remainingChatRequests / maxRequest) * 100;

  return (
    <div className={styles["progressBar"]}>
      <div className={styles["progressBar__header"]}>
        <p className={styles["progressBar__label"]}>残りリクエスト</p>
        <p className={styles["progressBar__value"]}>
          <span className={styles["progressBar__accent"]}>
            {organization.remainingChatRequests.toLocaleString()}
          </span>
          {organization.plan.type !== "free" ? (
            <>/{(organization.plan.limits.monthlyRequestLimit || 0).toLocaleString()}</>
          ) : (
            <>/ 50</>
          )}
        </p>
      </div>
      <div className={styles["progressBar__track"]}>
        <div className={styles["progressBar__fill"]} style={{ width: `${remainingPercent}%` }} />
      </div>
    </div>
  );
};

// クレジット履歴テーブル
const CreditsTable = (credits: Credit[], loading: boolean, errorMessage?: string | null) => {
  const columns: ColumnDef<Credit>[] = [
    {
      header: "日付",
      accessorKey: "createdAtText",
    },
    {
      header: "プロジェクト",
      cell: ({ row }) => {
        return row.original.projectName || "-";
      },
    },
    {
      header: "アクション",
      accessorKey: "purposeName",
    },
    {
      header: "クレジット使用/付与",
      cell: ({ row }) => {
        return row.original.type === "add" ? (
          <p className="text-green">+{row.original.amount}</p>
        ) : (
          <p className="text-red">-{row.original.amount}</p>
        );
      },
    },
    {
      header: "実行者",
      cell: ({ row }) => {
        return row.original.created_by ? (
          <div className="flex flex-row items-center gap-1">
            <UserIcon />
            <p>{row.original.createdByName}</p>
          </div>
        ) : (
          <p>-</p>
        );
      },
    },
  ];

  const table = useReactTable({
    data: credits,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      <table className={styles["table"]}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length}>
                <Loading />
              </td>
            </tr>
          ) : errorMessage ? (
            <tr>
              <td colSpan={columns.length} className="text-center text-red-500">
                <div className="text-center w-full">{errorMessage}</div>
              </td>
            </tr>
          ) : credits.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="text-center">
                <div className="text-center w-full">データがありません</div>
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    <div>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// チャットリクエスト変換モーダル
interface ChatRequestConvertModalProps {
  organization: Organization;
  currentUser: User;
  onClose: () => void;
}

const ChatRequestConvertModal: React.FC<ChatRequestConvertModalProps> = ({
  organization,
  currentUser: _currentUser,
  onClose,
}) => {
  // 入力は「追加チャットリクエスト数」
  const [addedRequests, setAddedRequests] = useState<string>("");
  const [isConverting, setIsConverting] = useState(false);
  const dispatch = useDispatch();

  const convertedChatRequests = useMemo(() => {
    const req = parseInt(addedRequests, 10) || 0;
    return organization.remainingChatRequests + req;
  }, [addedRequests, organization.remainingChatRequests]);

  // 増加分は入力値そのもの（addedRequests）

  // 変換可能な最大クレジット数
  const maxConvertibleCredits = organization.remainingCredit;

  // 必要クレジット数（1クレジット = 10 リクエスト）
  const requiredCredits = useMemo(() => {
    const req = parseInt(addedRequests, 10) || 0;
    return Math.ceil(req / 10);
  }, [addedRequests]);

  // バリデーション
  const isValidAmount = useMemo(() => {
    const req = parseInt(addedRequests, 10);
    if (!req || req <= 0) return false;
    // 10の倍数制約（クレジットが整数のため）
    if (req % 10 !== 0) return false;
    return requiredCredits > 0 && requiredCredits <= maxConvertibleCredits;
  }, [addedRequests, maxConvertibleCredits, requiredCredits]);

  // エラーメッセージ（入力直下に表示）
  const errorMessage = useMemo(() => {
    if (!addedRequests) return "";
    const v = parseInt(addedRequests, 10) || 0;
    if (v <= 0) return "1以上の数値を入力してください";
    if (v % 10 !== 0) return "10リクエスト単位で入力してください";
    if (requiredCredits > maxConvertibleCredits) return "クレジットが足りません";
    return "";
  }, [addedRequests, requiredCredits, maxConvertibleCredits]);

  const handleConvert = async () => {
    if (!isValidAmount) return;

    setIsConverting(true);
    const organizationApi = new OrganizationApi();
    const result = await organizationApi.convertCreditToChatRequest(
      organization.id,
      Number(requiredCredits),
    );
    if (result) {
      dispatch(
        setOrganization({
          ...organization,
          remainingCredit: organization.remainingCredit - Number(requiredCredits),
          remainingChatRequests:
            organization.remainingChatRequests + (parseInt(addedRequests, 10) || 0),
        }),
      );
      toast.success(TOAST_MESSAGES.BILLING.CHAT_REQUEST_ADD_SUCCESS);
      onClose();
    } else {
      toast.error(TOAST_MESSAGES.BILLING.CHAT_REQUEST_ADD_FAILED);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  // 数値フォーマット
  const formatNumber = (n: number) => n.toLocaleString();

  return (
    <div className="p-6">
      <div className="space-y-5">
        {/* 説明 + 入力フォーム */}
        <div className="space-y-4">
          <div>
            <p className="text-base text-gray-700 mb-6">
              クレジットを消費してチャットリクエストを追加できます。
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              追加チャットリクエスト数
              <input
                type="number"
                value={addedRequests}
                onChange={(e) => {
                  const value = e.target.value;
                  // 数値のみ許可（空文字、正の整数のみ）
                  if (value === "" || /^\d+$/.test(value)) {
                    setAddedRequests(value);
                  }
                }}
                placeholder="例: 20"
                min="10"
                step={10}
                className={`${styles["input"]} font-medium`}
              />
            </label>
            <div className={`mt-2 text-xs ${styles["rateText"]}`}>
              1クレジットで10リクエスト追加できます
            </div>
            {errorMessage && (
              <p className={`mt-1 text-xs ${styles["errorText"]}`}>{errorMessage}</p>
            )}
          </div>

          {/* プレビュー（行内で → で表示） */}
          <div className={styles["previewCard"]}>
            <div className="grid grid-cols-2 gap-4">
              {/* クレジット数 */}
              <div>
                <p className="text-xs text-gray-500 mb-1">クレジット数</p>
                <div className={styles["valueRow"]}>
                  <span
                    className={`text-xl font-bold ${addedRequests && isValidAmount ? styles["beforeDim"] : ""}`}
                  >
                    {formatNumber(organization.remainingCredit)}
                  </span>
                  {addedRequests && isValidAmount && (
                    <>
                      <span className={styles["arrowInline"]}>→</span>
                      <span className={`text-xl font-bold ${styles["accentText"]}`}>
                        {formatNumber(organization.remainingCredit - requiredCredits)}
                      </span>
                      <span className={styles["deltaInline"]}>
                        ({`-${formatNumber(requiredCredits)}`})
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* リクエスト数 */}
              <div>
                <p className="text-xs text-gray-500 mb-1">リクエスト数</p>
                <div className={styles["valueRow"]}>
                  <span
                    className={`text-xl font-bold ${addedRequests && isValidAmount ? styles["beforeDim"] : ""}`}
                  >
                    {formatNumber(organization.remainingChatRequests)}
                  </span>
                  {addedRequests && isValidAmount && (
                    <>
                      <span className={styles["arrowInline"]}>→</span>
                      <span className={`text-xl font-bold ${styles["accentText"]}`}>
                        {formatNumber(convertedChatRequests)}
                      </span>
                      <span className={styles["deltaInline"]}>
                        ({`+${formatNumber(parseInt(addedRequests, 10) || 0)}`})
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 入力直下に移設済み */}
        </div>

        {/* 注意事項 */}
        <div className={`${styles["noteSection"]} p-4 rounded-lg`}>
          <h4 className="text-sm font-medium text-gray-800 mb-2">注意事項</h4>
          <ul className="text-xs text-gray-700 space-y-1">
            <li>• 変換レート: 1クレジット = 10リクエスト</li>
            <li>• クレジットをチャットリクエストに変換すると、元のクレジットは消費されます</li>
            <li>• 変換は取り消すことができません</li>
            <li>• 変換後のチャットリクエストは即座に利用可能になります</li>
          </ul>
        </div>

        {/* 最終サマリー + ボタン */}
        {addedRequests && isValidAmount && (
          <div className="text-sm text-gray-700 flex justify-end pt-4">
            消費: {formatNumber(requiredCredits)}クレジット / 増加: +
            {formatNumber(parseInt(addedRequests, 10) || 0)}リクエスト
          </div>
        )}
        <div className="flex justify-end space-x-3 pt-2">
          <CommonButton variant="label" onClick={handleCancel}>
            キャンセル
          </CommonButton>
          <CommonButton
            variant="filled"
            onClick={handleConvert}
            disabled={!isValidAmount || isConverting}
          >
            {isConverting ? "追加中..." : "追加する"}
          </CommonButton>
        </div>
      </div>
    </div>
  );
};

export default Billings;
